import { UnreadableSummaryError } from './normalize';

/**
 * What a failed summary request means for the page.
 *
 * - `absent`      — the route is not on this server (404/405). Not an error: say so plainly.
 * - `outdated`    — the server answered with a body this page cannot read.
 * - `rateLimited` — 429. Never retried app-wide; gets its own wording.
 * - `forbidden`   — 403: not this party's election, or no access.
 * - `error`       — anything else, with a retry.
 *
 * Only 404/405 count as absent. A non-404 proves nothing about whether the route is deployed
 * (a 401 can come from the auth layer before routing), so it is never read as "present".
 */
export type SummaryFailure = 'absent' | 'outdated' | 'rateLimited' | 'forbidden' | 'error';

export const httpStatusOf = (error: unknown): number | undefined =>
	(error as { response?: { status?: number } } | null | undefined)?.response?.status;

export const classifySummaryError = (error: unknown): SummaryFailure => {
	if (error instanceof UnreadableSummaryError) return 'outdated';
	const status = httpStatusOf(error);
	if (status === 404 || status === 405) return 'absent';
	if (status === 429) return 'rateLimited';
	if (status === 403) return 'forbidden';
	return 'error';
};

/** Absent and outdated will not fix themselves on a retry within the session. */
export const shouldRetrySummary = (failureCount: number, error: unknown): boolean => {
	const kind = classifySummaryError(error);
	if (kind === 'absent' || kind === 'outdated' || kind === 'rateLimited' || kind === 'forbidden') return false;
	return failureCount < 2;
};
